import React, { useState, useEffect, useRef } from 'react';
import './Salon.css';

/**
 * Reusable date picker button component for calendar navigation.
 * Displays the current date/month and allows users to select a new date.
 * 
 * @param {Date} currentDate - The current date being displayed
 * @param {string} viewMode - 'day', 'week', or 'month'
 * @param {Function} onDateChange - Callback when date is selected (receives Date object)
 * @param {Function} getMonthName - Function to get localized month name
 * @param {Function} getDayName - Function to get localized day name
 * @param {string} className - Additional CSS classes
 */
const DatePickerButton = ({ 
  currentDate, 
  viewMode, 
  onDateChange, 
  getMonthName, 
  getDayName,
  className = '' 
}) => {
  const dateInputRef = useRef(null);
  const isPickerOpenRef = useRef(false);
  const pendingValueRef = useRef(null);
  const valueWhenOpenedRef = useRef(null);
  
  // Format date for input (YYYY-MM-DD)
  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [inputValue, setInputValue] = useState(() => formatDateForInput(currentDate));

  // Format date for display based on view mode
  const getDisplayText = () => {
    if (viewMode === 'month') {
      return `${getMonthName(currentDate.getMonth())} ${currentDate.getFullYear()}`;
    } else if (viewMode === 'week') {
      // For week view, show the range
      const weekStart = new Date(currentDate);
      const day = weekStart.getDay();
      const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1); // Monday start
      weekStart.setDate(diff);
      
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      
      return `${getMonthName(weekStart.getMonth())} ${weekStart.getDate()} - ${getMonthName(weekEnd.getMonth())} ${weekEnd.getDate()}, ${weekEnd.getFullYear()}`;
    } else {
      // Day view
      return `${getDayName(currentDate.getDay())}, ${getMonthName(currentDate.getMonth())} ${currentDate.getDate()}, ${currentDate.getFullYear()}`;
    }
  };

  // Initialize input value from currentDate, but only when input is not focused
  // This prevents React from updating the value while the picker is open
  useEffect(() => {
    if (dateInputRef.current && !isPickerOpenRef.current) {
      const isFocused = document.activeElement === dateInputRef.current;
      if (!isFocused) {
        setInputValue(formatDateForInput(currentDate));
      }
    }
  }, [currentDate]);

  const handleButtonClick = () => {
    if (dateInputRef.current) {
      // Capture the current value when opening the picker
      valueWhenOpenedRef.current = inputValue;
      isPickerOpenRef.current = true;
      dateInputRef.current.showPicker?.() || dateInputRef.current.click();
    }
  };

  const handleDateChange = (e) => {
    const newValue = e.target.value;
    pendingValueRef.current = newValue;
    setInputValue(newValue);
    
    // Check if this looks like a date selection (not just month navigation)
    // If the day changed, it's likely a date selection
    if (valueWhenOpenedRef.current) {
      const openedDate = new Date(valueWhenOpenedRef.current);
      const newDate = new Date(newValue);
      const dayChanged = openedDate.getDate() !== newDate.getDate();
      
      // If day changed, this is likely a date selection - commit immediately with short delay
      // This handles the case where blur might not fire reliably
      if (dayChanged) {
        // Use a short delay to allow picker to close, then commit
        setTimeout(() => {
          if (pendingValueRef.current === newValue) {
            const selectedDate = new Date(newValue);
            if (!isNaN(selectedDate.getTime())) {
              isPickerOpenRef.current = false;
              pendingValueRef.current = null;
              onDateChange(selectedDate);
            }
          }
        }, 150);
      }
    }
    // Otherwise, wait for blur to commit (month navigation case)
  };

  const handleInputFocus = () => {
    isPickerOpenRef.current = true;
    // Also capture value on focus as a fallback (in case button click doesn't set it)
    if (!valueWhenOpenedRef.current) {
      valueWhenOpenedRef.current = inputValue;
    }
  };

  // Use a delayed blur check to commit the date change when picker actually closes
  // This is a fallback for cases where day-change detection doesn't work
  const handleInputBlur = () => {
    // Wait before checking - month navigation causes blur but picker stays open
    setTimeout(() => {
      const stillFocused = document.activeElement === dateInputRef.current;
      // Only commit if input is definitely not focused (picker closed) and we still have a pending value
      // (if day changed, it was already committed in handleDateChange)
      if (!stillFocused && pendingValueRef.current) {
        isPickerOpenRef.current = false;
        const selectedDate = new Date(pendingValueRef.current);
        if (!isNaN(selectedDate.getTime())) {
          onDateChange(selectedDate);
        }
        pendingValueRef.current = null;
      } else if (!stillFocused) {
        isPickerOpenRef.current = false;
        // No pending change, sync with currentDate
        setInputValue(formatDateForInput(currentDate));
      }
    }, 200);
  };

  return (
    <div className={`date-picker-button-wrapper ${className}`}>
      <button
        type="button"
        className="date-picker-button"
        onClick={handleButtonClick}
        title="Click to select date"
      >
        {getDisplayText()}
      </button>
      <input
        ref={dateInputRef}
        type="date"
        value={inputValue}
        onChange={handleDateChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        className="date-picker-input-hidden"
        aria-label="Select date"
      />
    </div>
  );
};

export default DatePickerButton;
